//adminfrontend/src/admin_ui/pages/AdminStockMovementsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function AdminStockMovementsPage({ adminEmail, onLogout }) {
  const [searchParams] = useSearchParams();
  const initialBranchIdRef = useRef(null);
  const initialLocationIdRef = useRef(null);
  const initialProductIdRef = useRef(null);
  const initialBatchIdRef = useRef(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");

  const [productId, setProductId] = useState("");
  const [batchId, setBatchId] = useState("");

  const [movementType, setMovementType] = useState("");
  const [reason, setReason] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectedBranch = useMemo(
    () => branches.find((b) => String(b.id) === String(branchId)) || null,
    [branches, branchId]
  );

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  async function loadBranches() {
    const data = await adminInventoryApi.listBranches();
    if (!data || data.ok !== true) throw new Error("branches_failed");
    const bs = data.branches || [];
    setBranches(bs);
    const preferred =
      branchId ||
      initialBranchIdRef.current ||
      "";
    const preferredExists = preferred && bs.some((b) => String(b.id) === String(preferred));
    if (!branchId && preferredExists) setBranchId(String(preferred));
    if (!branchId && !preferredExists && bs[0]) setBranchId(String(bs[0].id));
    return bs;
  }

  async function loadLocations(bId) {
    if (!bId) {
      setLocations([]);
      return;
    }
    const data = await adminInventoryApi.listLocations(bId);
    if (!data || data.ok !== true) throw new Error("locations_failed");
    setLocations(data.locations || []);
  }

  async function loadMovements() {
    if (!branchId) return;
    setLoading(true);
    setError("");

    try {
      const params = {
        branchId: Number(branchId),
        limit,
        offset,
      };

      if (locationId) params.locationId = Number(locationId);
      if (productId) params.productId = Number(productId);
      if (batchId) params.batchId = Number(batchId);
      if (movementType) params.movementType = movementType;
      if (reason) params.reason = reason;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const data = await adminInventoryApi.listStockMovements(params);
      if (!data || data.ok !== true) throw new Error("movements_failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      console.error("[AdminStockMovementsPage] load error:", e);
      setError("Unable to load stock movements. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function fullInitLoad() {
    setLoading(true);
    setError("");
    try {
      const bs = await loadBranches();
      const effectiveBranchId = branchId || (bs[0] ? String(bs[0].id) : "");
      if (effectiveBranchId) await loadLocations(effectiveBranchId);
    } catch (e) {
      console.error("[AdminStockMovementsPage] init error:", e);
      setError("Unable to load branches/locations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const qBranchId = searchParams.get("branchId");
    const qLocationId = searchParams.get("locationId");
    const qProductId = searchParams.get("productId");
    const qBatchId = searchParams.get("batchId");

    if (qBranchId) {
      const v = String(qBranchId).replace(/[^\d]/g, "");
      if (v) initialBranchIdRef.current = v;
    }
    if (qLocationId) {
      const v = String(qLocationId).replace(/[^\d]/g, "");
      if (v) initialLocationIdRef.current = v;
    }
    if (qProductId) {
      const v = String(qProductId).replace(/[^\d]/g, "");
      if (v) initialProductIdRef.current = v;
    }
    if (qBatchId) {
      const v = String(qBatchId).replace(/[^\d]/g, "");
      if (v) initialBatchIdRef.current = v;
    }

    // prefill inputs early (even before branches load)
    if (initialBranchIdRef.current) setBranchId(initialBranchIdRef.current);
    if (initialLocationIdRef.current) setLocationId(initialLocationIdRef.current);
    if (initialProductIdRef.current) setProductId(initialProductIdRef.current);
    if (initialBatchIdRef.current) setBatchId(initialBatchIdRef.current);

    fullInitLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOffset(0);
    loadLocations(branchId).catch(() => {});
    // Only clear location on branch change if it wasn't set via initial query.
    if (initialLocationIdRef.current) {
      // consume it so future branch changes behave normally
      initialLocationIdRef.current = null;
    } else {
      setLocationId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, locationId, productId, batchId, movementType, reason, dateFrom, dateTo, limit, offset]);

  function onApply() {
    setOffset(0);
    loadMovements().catch(() => {});
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Stock Movements</h1>
            <p className="ucx-admin-subtitle">
              Movement history for a branch with filters (product, batch, location, reason).
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}

            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={onApply} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>

          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ""}
                </option>
              ))}
            </select>

            <select value={locationId} onChange={(e) => { setOffset(0); setLocationId(e.target.value); }}>
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} {l.code ? `(${l.code})` : ""} [{l.type}]
                </option>
              ))}
            </select>

            <input
              type="text"
              value={productId}
              onChange={(e) => {
                setOffset(0);
                setProductId(e.target.value.replace(/[^\d]/g, ""));
              }}
              placeholder="Product ID (optional)"
            />

            <input
              type="text"
              value={batchId}
              onChange={(e) => {
                setOffset(0);
                setBatchId(e.target.value.replace(/[^\d]/g, ""));
              }}
              placeholder="Batch ID (optional)"
            />

            <select value={movementType} onChange={(e) => { setOffset(0); setMovementType(e.target.value); }}>
              <option value="">All types</option>
              <option value="in">in</option>
              <option value="out">out</option>
              <option value="transfer">transfer</option>
            </select>

            <select value={reason} onChange={(e) => { setOffset(0); setReason(e.target.value); }}>
              <option value="">All reasons</option>
              <option value="purchase">purchase</option>
              <option value="sale">sale</option>
              <option value="manufactured">manufactured</option>
              <option value="writeoff">writeoff</option>
              <option value="correction">correction</option>
              <option value="transfer">transfer</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setOffset(0);
                setDateFrom(e.target.value);
              }}
              title="Date From"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setOffset(0);
                setDateTo(e.target.value);
              }}
              title="Date To"
            />

            <select
              value={limit}
              onChange={(e) => {
                setOffset(0);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>

            <button type="button" className="ucx-btn ucx-btn-primary" onClick={onApply} disabled={loading}>
              Apply
            </button>
          </div>

          <div className="ucx-admin-toolbar">
            <div className="ucx-admin-toolbar-hint">
              Branch: <b>{selectedBranch ? selectedBranch.name : "—"}</b>
            </div>

            <div className="ucx-admin-pager">
              <span className="ucx-small">
                Showing{" "}
                <b>{total === 0 ? 0 : Math.min(total, offset + 1)}</b>–<b>{Math.min(total, offset + limit)}</b>{" "}
                of <b>{total}</b>
              </span>

              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                disabled={!canPrev || loading}
              >
                Prev
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => o + limit)}
                disabled={!canNext || loading}
              >
                Next
              </button>
            </div>
          </div>

          {!!error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Time</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Product</th>
                <th>Batch</th>
                <th>Qty</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</td>
                  <td>{r.movement_type || "-"}</td>
                  <td>{r.reason || "-"}</td>
                  <td>
                    {r.product_id}
                    {r.product_name ? ` — ${r.product_name}` : ""}
                  </td>
                  <td>{r.batch_id ? `${r.batch_id} (${r.ucx_batch || "-"})` : "-"}</td>
                  <td>{r.quantity}</td>
                  <td>
                    {r.from_location_name
                      ? `${r.from_location_name}${r.from_location_code ? ` (${r.from_location_code})` : ""}`
                      : "-"}
                  </td>
                  <td>
                    {r.to_location_name
                      ? `${r.to_location_name}${r.to_location_code ? ` (${r.to_location_code})` : ""}`
                      : "-"}
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={9} className="ucx-admin-empty">
                    Loading stock movements…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="ucx-admin-empty">
                    No stock movements found for these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminStockMovementsPage;
